/**
 * PeriodBar — Epic 4 (Beoordelen op kerntaken), Story 4.3 (S5, AC #1)
 *
 * Horizontale balk over de maanden van het (naar hele kalendermaanden
 * genormaliseerde) beoordelingsvenster. Per tijdvak een segment met label
 * "Pakket A · jan–mrt (3/12)" — de noemer is `totalCoveredMonths` uit
 * `resolvePeriodsAndWeights`, NIET hardcoded 12 (een tussenbeoordeling kan
 * bijv. een 6-maands venster hebben -> "(2/6)").
 */

import { PACKAGE_LETTERS } from './periodLabels';

const MONTH_LABELS_NL = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

function monthLabel(isoDate: string): string {
  const month = Number(isoDate.slice(5, 7));
  return MONTH_LABELS_NL[month - 1] ?? '';
}

/** Segmentkleuren, afwisselend per tijdvak-volgorde (navy/teal, NFR-1). */
const SEGMENT_COLORS = ['bg-[#2F5A7A]', 'bg-[#54949E]'];

export interface PeriodBarSegment {
  periodId: number;
  startDate: string;
  endDate: string | null;
  months: number;
}

export interface PeriodBarProps {
  segments: PeriodBarSegment[];
  totalCoveredMonths: number;
}

export function PeriodBar({ segments, totalCoveredMonths }: PeriodBarProps) {
  if (totalCoveredMonths === 0) return null;

  return (
    <div data-testid="period-bar" className="space-y-2">
      <div className="flex w-full h-3 rounded-full overflow-hidden border border-[#E2E8F0]">
        {segments.map((segment, index) => (
          <div
            key={segment.periodId}
            data-testid={`period-bar-segment-${segment.periodId}`}
            className={SEGMENT_COLORS[index % SEGMENT_COLORS.length]}
            style={{ width: `${(segment.months / totalCoveredMonths) * 100}%` }}
            title={`Pakket ${PACKAGE_LETTERS[index % PACKAGE_LETTERS.length]}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        {segments.map((segment, index) => {
          const endLabel = segment.endDate ? monthLabel(segment.endDate) : 'heden';
          return (
            <span key={segment.periodId} data-testid={`period-bar-label-${segment.periodId}`}>
              Pakket {PACKAGE_LETTERS[index % PACKAGE_LETTERS.length]} &middot; {monthLabel(segment.startDate)}
              &ndash;{endLabel} ({segment.months}/{totalCoveredMonths})
            </span>
          );
        })}
      </div>
    </div>
  );
}
